import { useState } from 'react'
import { colors, fonts } from './theme'
import SectionBar from './components/SectionBar'

export default function ResearchVault() {
  const [selectedView, setSelectedView] = useState('papers')

  const views = [
    { key: 'papers', label: 'Research Papers' },
    { key: 'thesis_notes', label: 'Thesis Notes' },
    { key: 'collab', label: 'Collaboration' },
    { key: 'reading_list', label: 'Reading List' },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.subNav}>
        {views.map(v => (
          <button
            key={v.key}
            onClick={() => setSelectedView(v.key)}
            style={{
              ...styles.subTab,
              ...(selectedView === v.key ? styles.subTabActive : {}),
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {selectedView === 'papers' && <PapersView />}
        {selectedView === 'thesis_notes' && <ThesisNotesView />}
        {selectedView === 'collab' && <CollaborationView />}
        {selectedView === 'reading_list' && <ReadingListView />}
      </div>
    </div>
  )
}

function PapersView() {
  return (
    <>
      <SectionBar title="Key Research" subtitle="Foundational papers and analyses" accent="gold" />

      <div style={styles.papersGrid}>
        <PaperCard
          title="What's Really Inside Your Private Credit Fund"
          source="MSCI Research"
          date="2024"
          keyInsights={[
            'Bayesian de-smoothing reveals true volatility 2-3x higher than reported',
            'Security-level analysis shows risk profiles vary wildly within same strategy label',
            'Fund aging causes systematic drift: senior debt % falls from 87% to 61% over time',
            'Spread duration compresses ~40% as funds age',
            'Young funds: 0.64 correlation to portfolio (poor diversifier). Mature: 0.25 (excellent)',
            'Risk decomposition: public factors vs. private credit factors vs. PE exposure',
          ]}
          actionItems={[
            'Build de-smoothing model for BDC NAV analysis',
            'Track senior debt % and spread duration for aging analysis',
            'Calculate portfolio-level correlation for each BDC position',
          ]}
        />
        <PaperCard
          title="Confidence Game"
          source="Christine Richard (on William Ackman)"
          date="2010"
          keyInsights={[
            'Deep forensic analysis of MBIA — proving that stated reserves were inadequate',
            'Ackman spent 5+ years building his thesis before the market recognized the risk',
            'Key principle: understand the business better than management claims to understand it',
            'Follow the cash — accounting income can be manufactured, cash flow cannot',
            'The importance of studying the liabilities side, not just the assets',
            'Regulatory capture: when rating agencies and regulators fail, independent analysis matters most',
          ]}
          actionItems={[
            'Apply MBIA-style liability analysis to CLO structures',
            'Build borrower-level cash flow models for top BDC holdings',
            'Track rating agency methodology changes for CLO tranches',
          ]}
        />
        <PaperCard
          title="Complexity Economics"
          source="J. Doyne Farmer"
          date="Ongoing"
          keyInsights={[
            'Financial markets are complex adaptive systems, not equilibrium systems',
            'Agent-based models capture emergent behavior that equilibrium models miss',
            'Feedback loops between agents create amplification and non-linear dynamics',
            'Leverage is the key amplifier — small shocks become large through leverage cascades',
            'Diversity of strategies stabilizes markets; herding destabilizes them',
            'Traditional risk models (VaR, correlation) systematically underestimate tail risk',
          ]}
          actionItems={[
            'Build agent-based simulation of PE-CLO-BDC ecosystem',
            'Model feedback loops between CLO OC tests and loan prices',
            'Simulate leverage cascade scenarios with different starting conditions',
          ]}
        />
      </div>
    </>
  )
}

function ThesisNotesView() {
  return (
    <>
      <SectionBar title="Investment Thesis Notes" subtitle="Dave and Lori's analysis" accent="gold" />

      <div style={styles.thesisGrid}>
        <ThesisCard
          author="Dave"
          date="March 2026"
          thesis="Blue Owl LEAP Options"
          status="Active"
          notes={[
            'Blue Owl (OWL) operates permanent capital vehicles — no redemption pressure',
            'Exploring LEAP call options for leveraged exposure to the private credit thesis',
            'Key question: does the options market properly price BDC earnings stability?',
            'PE-CLO symbiosis is the macro backdrop — CLOs buy 70% of leveraged loans, PE creates 60% of supply',
            'Leverage stack concern: 5x company → 10x CLO → 10x repo = 500x cumulative. But AAA has 0% historical loss rate',
            'The doom loop is theoretical — subordination and active management have prevented it historically',
          ]}
          openQuestions={[
            'What is the right strike and expiry for OWL LEAPs?',
            'How does OWL NII sensitivity compare to peers under rate scenarios?',
            'What percentage of OWL portfolio is first lien senior secured?',
            'How concentrated is OWL by borrower / sector?',
            'What is OWL dividend coverage under stress (SOFR -200bp + 5% defaults)?',
          ]}
        />
        <ThesisCard
          author="Lori"
          date="April 2026"
          thesis="Systemic Risk & Agent-Based Analysis"
          status="Research Phase"
          notes={[
            'Applying Doyne Farmer complex systems lens to private credit ecosystem',
            'Focus: understanding feedback loops and emergent behavior in the PE-CLO-BDC network',
            'MSCI research validates that fund-level analysis hides security-level risk divergence',
            'Key insight: institutions behave as agents with distinct incentives, constraints, and information',
            'Rating agencies are pro-cyclical amplifiers, not stabilizers',
            'Repo market is the critical vulnerability — margin spirals can cascade in hours',
          ]}
          openQuestions={[
            'Can we build a simplified ABM of the leverage stack?',
            'What data is publicly available for CLO OC test monitoring?',
            'How correlated are PE-backed borrower defaults in practice?',
            'What is the actual distribution of CLO equity returns through cycles?',
            'How do we measure "herding" in CLO manager loan selection?',
          ]}
        />
      </div>
    </>
  )
}

function CollaborationView() {
  return (
    <>
      <SectionBar title="Collaboration Space" subtitle="Dave + Lori — 13 years of partnership" accent="gold" />

      <div style={styles.collabHeader}>
        <div style={styles.collabUser}>
          <div style={styles.collabAvatar}>D</div>
          <div>
            <div style={styles.collabName}>Dave</div>
            <div style={styles.collabFocus}>LEAP Options + Blue Owl Thesis</div>
          </div>
        </div>
        <div style={styles.collabDivider}>+</div>
        <div style={styles.collabUser}>
          <div style={{ ...styles.collabAvatar, background: colors.steelBg, color: colors.steel }}>L</div>
          <div>
            <div style={styles.collabName}>Lori</div>
            <div style={styles.collabFocus}>Systems Analysis + Agent Modeling</div>
          </div>
        </div>
      </div>

      <SectionBar title="Shared Research Agenda" />

      <div style={styles.agendaList}>
        <AgendaItem
          priority="high"
          title="Blue Owl Deep Dive"
          desc="Complete fundamental analysis of OWL: portfolio quality, NII sensitivity, leverage, management. Map to the due diligence framework in Mechanics Lab."
          owner="Dave (lead) + Lori (review)"
        />
        <AgendaItem
          priority="high"
          title="LEAP Options Analysis"
          desc="Identify optimal strike/expiry for OWL LEAPs. Model payoff scenarios under different rate and credit cycle assumptions. Connect to Armstrong for options analytics."
          owner="Dave (lead)"
        />
        <AgendaItem
          priority="medium"
          title="Agent-Based Model Prototype"
          desc="Build simplified simulation of the PE → CLO → Repo leverage chain. Model how shocks propagate. Use Doyne Farmer's framework for agent design."
          owner="Lori (lead)"
        />
        <AgendaItem
          priority="medium"
          title="Peer Comparison"
          desc="Compare OWL to ARCC, BXSL, MAIN, and other BDCs on the due diligence framework. Identify where OWL has structural advantages and disadvantages."
          owner="Joint"
        />
        <AgendaItem
          priority="low"
          title="Live Data Integration"
          desc="Connect real-time price feeds, earnings data, and portfolio disclosures for tracked institutions. Build automated monitoring alerts."
          owner="Lori (lead)"
        />
      </div>

      <SectionBar title="Discussion Log" subtitle="Key decisions and insights" />

      <div style={styles.comingSoon}>
        <p style={styles.comingSoonText}>
          Discussion log coming soon — a shared space for capturing insights, debating thesis points,
          and tracking how your understanding evolves over time.
        </p>
        <div style={styles.comingSoonBadge}>Under Construction</div>
      </div>
    </>
  )
}

function ReadingListView() {
  return (
    <>
      <SectionBar title="Essential Reading" subtitle="Curated for private credit mastery" accent="gold" />

      <div style={styles.categorySection}>
        <div style={styles.categoryTitle}>Foundational</div>
        <ReadingItem title="Confidence Game" author="Christine Richard" type="Book" desc="The definitive account of Ackman's MBIA short — a masterclass in forensic financial analysis and conviction investing." />
        <ReadingItem title="The Big Short" author="Michael Lewis" type="Book" desc="How a few investors saw the subprime crisis coming. Relevant for understanding CDO/CLO structures and the limits of rating agency models." />
        <ReadingItem title="When Genius Failed" author="Roger Lowenstein" type="Book" desc="The collapse of Long-Term Capital Management. Essential for understanding leverage, liquidity risk, and contagion in credit markets." />
        <ReadingItem title="Complexity Economics" author="J. Doyne Farmer" type="Papers" desc="Farmer's body of work on agent-based modeling of financial markets. Start with 'An ecological perspective on the future of computer trading.'" />
      </div>

      <div style={styles.categorySection}>
        <div style={styles.categoryTitle}>Private Credit Specific</div>
        <ReadingItem title="What's Really Inside Your Private Credit Fund" author="MSCI" type="Paper" desc="Security-level analysis reveals true risk profiles hidden by strategy labels. Core methodology for this platform." />
        <ReadingItem title="CLO Primer" author="J.P. Morgan / Citi Research" type="Report" desc="Investment bank primers on CLO mechanics, waterfall structures, and historical performance data." />
        <ReadingItem title="BDC Regulatory Framework" author="SEC" type="Regulatory" desc="Investment Company Act of 1940 provisions, SBCAA (2018 leverage expansion), and ongoing regulatory developments." />
        <ReadingItem title="Leveraged Loan Market Annual Review" author="LCD (PitchBook)" type="Data" desc="Annual data on leveraged loan issuance, CLO market, default rates, and recovery statistics." />
      </div>

      <div style={styles.categorySection}>
        <div style={styles.categoryTitle}>Systems Thinking & Risk</div>
        <ReadingItem title="Thinking in Systems" author="Donella Meadows" type="Book" desc="The classic introduction to systems dynamics. Feedback loops, leverage points, and unintended consequences." />
        <ReadingItem title="The Misbehavior of Markets" author="Benoit Mandelbrot" type="Book" desc="Why financial markets are far wilder than standard models assume. Fat tails, long memory, and fractal geometry." />
        <ReadingItem title="Antifragile" author="Nassim Taleb" type="Book" desc="Systems that gain from disorder. How to structure investments that benefit from volatility and tail events." />
        <ReadingItem title="Why Stock Markets Crash" author="Didier Sornette" type="Book" desc="Log-periodic oscillations and the predictability of large drawdowns. Mathematical approaches to bubble detection." />
      </div>
    </>
  )
}

// Sub-components
function PaperCard({ title, source, date, keyInsights, actionItems }) {
  return (
    <div style={styles.paperCard}>
      <div style={styles.paperTitle}>{title}</div>
      <div style={styles.paperMeta}>{source} &middot; {date}</div>
      <div style={styles.paperSection}>
        <div style={styles.paperLabel}>Key Insights</div>
        {keyInsights.map((insight, i) => (
          <div key={i} style={styles.paperInsight}>{insight}</div>
        ))}
      </div>
      <div style={styles.paperSection}>
        <div style={{ ...styles.paperLabel, color: colors.steel }}>Action Items</div>
        {actionItems.map((item, i) => (
          <div key={i} style={styles.paperAction}>{item}</div>
        ))}
      </div>
    </div>
  )
}

function ThesisCard({ author, date, thesis, status, notes, openQuestions }) {
  return (
    <div style={styles.thesisCard}>
      <div style={styles.thesisHeader}>
        <div>
          <div style={styles.thesisAuthor}>{author}</div>
          <div style={styles.thesisName}>{thesis}</div>
        </div>
        <div style={styles.thesisStatus}>{status}</div>
      </div>
      <div style={styles.thesisDate}>{date}</div>
      <div style={styles.thesisSection}>
        <div style={styles.thesisLabel}>Notes</div>
        {notes.map((n, i) => (
          <div key={i} style={styles.thesisNote}>{n}</div>
        ))}
      </div>
      <div style={styles.thesisSection}>
        <div style={{ ...styles.thesisLabel, color: colors.amberInk }}>Open Questions</div>
        {openQuestions.map((q, i) => (
          <div key={i} style={styles.thesisQuestion}>{q}</div>
        ))}
      </div>
    </div>
  )
}

function AgendaItem({ priority, title, desc, owner }) {
  const prioColor = priority === 'high' ? colors.redInk : priority === 'medium' ? colors.amberInk : colors.textMuted

  return (
    <div style={styles.agendaItem}>
      <div style={styles.agendaHeader}>
        <span style={{ ...styles.agendaPriority, color: prioColor }}>{priority.toUpperCase()}</span>
        <span style={styles.agendaTitle}>{title}</span>
      </div>
      <div style={styles.agendaDesc}>{desc}</div>
      <div style={styles.agendaOwner}>Owner: {owner}</div>
    </div>
  )
}

function ReadingItem({ title, author, type, desc }) {
  return (
    <div style={styles.readingItem}>
      <div style={styles.readingHeader}>
        <span style={styles.readingTitle}>{title}</span>
        <span style={styles.readingType}>{type}</span>
      </div>
      <div style={styles.readingAuthor}>{author}</div>
      <div style={styles.readingDesc}>{desc}</div>
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  subNav: {
    display: 'flex',
    gap: 2,
    padding: '8px 32px',
    borderBottom: `1px solid ${colors.rule}`,
    background: colors.navyDeep,
  },
  subTab: {
    padding: '4px 14px',
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 500,
    color: colors.textMuted,
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  subTabActive: {
    color: colors.gold,
    borderColor: colors.goldBorder,
    background: colors.goldBg,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  // Papers
  papersGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  paperCard: {
    padding: 20,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  paperTitle: {
    fontFamily: fonts.serif,
    fontSize: 18,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 4,
  },
  paperMeta: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
    marginBottom: 16,
  },
  paperSection: {
    marginBottom: 14,
  },
  paperLabel: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 6,
  },
  paperInsight: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    padding: '4px 0',
    paddingLeft: 12,
    borderBottom: `1px solid ${colors.ruleLight}`,
    lineHeight: 1.5,
  },
  paperAction: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.steel,
    padding: '4px 0',
    paddingLeft: 12,
    lineHeight: 1.5,
  },
  // Thesis
  thesisGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  thesisCard: {
    padding: 20,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  thesisHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  thesisAuthor: {
    fontFamily: fonts.mono,
    fontSize: 13,
    fontWeight: 600,
    color: colors.steel,
  },
  thesisName: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.textPrimary,
    marginTop: 2,
  },
  thesisStatus: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.greenInk,
    padding: '3px 8px',
    background: colors.greenBg,
    borderRadius: 2,
  },
  thesisDate: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
    marginBottom: 14,
  },
  thesisSection: {
    marginBottom: 12,
  },
  thesisLabel: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 6,
  },
  thesisNote: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    padding: '4px 0',
    paddingLeft: 12,
    lineHeight: 1.5,
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  thesisQuestion: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.amberInk,
    padding: '4px 0',
    paddingLeft: 12,
    lineHeight: 1.5,
  },
  // Collaboration
  collabHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 32,
    padding: 24,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  collabUser: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  collabAvatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: colors.goldBg,
    color: colors.gold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fonts.serif,
    fontSize: 20,
    fontWeight: 600,
    border: `2px solid ${colors.goldBorder}`,
  },
  collabName: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  collabFocus: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
  },
  collabDivider: {
    fontFamily: fonts.serif,
    fontSize: 24,
    color: colors.gold,
  },
  // Agenda
  agendaList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  agendaItem: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  agendaHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  agendaPriority: {
    fontFamily: fonts.mono,
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 2,
  },
  agendaTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  agendaDesc: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: 6,
  },
  agendaOwner: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
  },
  // Coming soon
  comingSoon: {
    padding: 24,
    background: colors.navySurface,
    border: `1px dashed ${colors.steelBorder}`,
    borderRadius: 3,
    textAlign: 'center',
  },
  comingSoonText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 1.6,
    marginBottom: 12,
  },
  comingSoonBadge: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.steel,
    padding: '4px 12px',
    border: `1px solid ${colors.steelBorder}`,
    borderRadius: 2,
    display: 'inline-block',
  },
  // Reading list
  categorySection: {
    marginBottom: 20,
  },
  categoryTitle: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `1px solid ${colors.rule}`,
  },
  readingItem: {
    padding: '12px 16px',
    background: colors.navySurface,
    border: `1px solid ${colors.ruleLight}`,
    borderRadius: 3,
    marginBottom: 6,
  },
  readingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  readingTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  readingType: {
    fontFamily: fonts.mono,
    fontSize: 10,
    color: colors.textMuted,
    padding: '2px 6px',
    border: `1px solid ${colors.ruleLight}`,
    borderRadius: 2,
  },
  readingAuthor: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.steel,
    marginBottom: 4,
  },
  readingDesc: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.5,
  },
}
